// src/components/audit/AuditModule.js
import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from "react";
import {
  Box, Container, Typography, Paper, Button, Grid,
  TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination,
  IconButton, Chip, Fade, InputAdornment,
  Tooltip, Skeleton, Stack, Menu, MenuItem,
  Alert, LinearProgress, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Divider,
  Tabs, Tab, Select, FormControl,
  AppBar, Toolbar, Slide, Card, CardContent,
  Collapse,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import AddIcon               from "@mui/icons-material/Add";
import SearchIcon            from "@mui/icons-material/Search";
import FileDownloadIcon      from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon      from "@mui/icons-material/PictureAsPdf";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreVertIcon          from "@mui/icons-material/MoreVert";
import EditIcon              from "@mui/icons-material/Edit";
import ClearIcon             from "@mui/icons-material/Clear";
import EventNoteIcon         from "@mui/icons-material/EventNote";
import PendingIcon           from "@mui/icons-material/Pending";
import PlayArrowIcon         from "@mui/icons-material/PlayArrow";
import DoneAllIcon           from "@mui/icons-material/DoneAll";
import ArchiveIcon           from "@mui/icons-material/Archive";
import RestoreIcon           from "@mui/icons-material/Restore";
import AssignmentIcon        from "@mui/icons-material/Assignment";
import SwapHorizIcon         from "@mui/icons-material/SwapHoriz";
import ErrorOutlineIcon      from "@mui/icons-material/ErrorOutline";
import VerifiedIcon          from "@mui/icons-material/Verified";
import VisibilityIcon        from "@mui/icons-material/Visibility";
import InventoryIcon         from "@mui/icons-material/Inventory";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LocationOnIcon        from "@mui/icons-material/LocationOn";
import CalendarMonthIcon     from "@mui/icons-material/CalendarMonth";
import PersonIcon            from "@mui/icons-material/Person";
import AccessTimeIcon        from "@mui/icons-material/AccessTime";
import CloseIcon             from "@mui/icons-material/Close";
import FilterListIcon        from "@mui/icons-material/FilterList";
import FilterListOffIcon     from "@mui/icons-material/FilterListOff";

import CreateEditAuditModal       from "../Modals/CreateEditAuditModal.js";
import { useAuth }                from "../../contexts/AuthContext";
import { helpDeskService }           from "../../services/helpDeskService";
import {
  fetchAuditEvents,
  fetchArchivedAuditEvents,
  restoreAuditEvent,
  updateAuditEvent,
  softDeleteAuditEvent,
  EVENT_STATUS_KEYS,
} from "../../services/auditService";

import {
  fetchAuditEventAssets,
  updateAuditHistoryStatus,
} from "../../services/auditHistoryService";
import { singleValueListService, AUDIT_VERIFICATION_STATUSES_LIST_NAME } from "../../services/singleValueListService";

/* ═══════════════════════════════════════════════════════════════════════════
   PDF LAZY LOADER
═══════════════════════════════════════════════════════════════════════════ */

let _jsPDFCache = null;

const getPDFLib = async () => {
  if (_jsPDFCache) return _jsPDFCache;
  const [{ default: jsPDF }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  if (typeof jsPDF.prototype.autoTable !== "function") {
    throw new Error("jspdf-autotable did not attach correctly");
  }
  _jsPDFCache = { jsPDF };
  return _jsPDFCache;
};

/* ═══════════════════════════════════════════════════════════════════════════
   PURE HELPERS
═══════════════════════════════════════════════════════════════════════════ */

const EVENT_STATUS = {
  initiated:   { ...EVENT_STATUS_KEYS.initiated,   icon: <PendingIcon   sx={{ fontSize: 13 }} /> },
  in_progress: { ...EVENT_STATUS_KEYS.in_progress, icon: <PlayArrowIcon sx={{ fontSize: 13 }} /> },
  completed:   { ...EVENT_STATUS_KEYS.completed,   icon: <DoneAllIcon   sx={{ fontSize: 13 }} /> },
};

const AUDIT_STATUS_FILTER_CFG = {
  all: {
    label:        "All Statuses",
    color:        "#374151",
    bgColor:      "#f3f4f6",
    borderColor:  "#d1d5db",
    activeColor:  "#1d4ed8",
    activeBg:     "#dbeafe",
    activeBorder: "#93c5fd",
    icon:         null,
  },
  initiated: {
    label:        "Initiated",
    color:        "#6b7280",
    bgColor:      "#f9fafb",
    borderColor:  "#e5e7eb",
    activeColor:  "#374151",
    activeBg:     "#f3f4f6",
    activeBorder: "#9ca3af",
    icon:         <PendingIcon   sx={{ fontSize: 14 }} />,
  },
  in_progress: {
    label:        "In Progress",
    color:        "#1d4ed8",
    bgColor:      "#eff6ff",
    borderColor:  "#bfdbfe",
    activeColor:  "#1d4ed8",
    activeBg:     "#dbeafe",
    activeBorder: "#60a5fa",
    icon:         <PlayArrowIcon sx={{ fontSize: 14 }} />,
  },
  completed: {
    label:        "Completed",
    color:        "#16a34a",
    bgColor:      "#f0fdf4",
    borderColor:  "#bbf7d0",
    activeColor:  "#15803d",
    activeBg:     "#dcfce7",
    activeBorder: "#4ade80",
    icon:         <DoneAllIcon   sx={{ fontSize: 14 }} />,
  },
};

const AUDIT_STATUS_FILTER_KEYS = ["all", "initiated", "in_progress", "completed"];

const ASSET_STATUS_CFG = {
  pending: {
    label: "Pending",
    color: "#6b7280", bgColor: "#f9fafb", borderColor: "#e5e7eb",
    icon: <PendingIcon sx={{ fontSize: 16 }} />,
  },
  verified: {
    label: "Verified",
    color: "#16a34a", bgColor: "#f0fdf4", borderColor: "#bbf7d0",
    icon: <VerifiedIcon sx={{ fontSize: 16 }} />,
  },
  missing: {
    label: "Missing",
    color: "#dc2626", bgColor: "#fef2f2", borderColor: "#fecaca",
    icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
  },
  shifted: {
    label: "Shifted",
    color: "#d97706", bgColor: "#fffbeb", borderColor: "#fde68a",
    icon: <SwapHorizIcon sx={{ fontSize: 16 }} />,
  },
};

const sanitizeCSV = (value) => {
  const str = String(value ?? "");
  if (/^[=+\-@\t\r]/.test(str)) return `'${str}`;
  return str;
};

const looksLikeUID = (val) => {
  if (!val || typeof val !== "string") return true;
  const trimmed = val.trim();
  if (!trimmed) return true;
  return /^[A-Za-z0-9_-]{20,}$/.test(trimmed) && !/\s/.test(trimmed);
};

const getCreatedByName = (ev) => {
  const c = ev?.metadata?.createdBy;
  if (c && !looksLikeUID(c)) return c;
  return "—";
};

const getAuditorName = (ev) => {
  const candidates = [
    ev?.auditor_email,
    ev?.auditor_id,
  ];
  for (const c of candidates) {
    if (c && !looksLikeUID(c)) return c;
  }
  return "—";
};

const getCreatedAt = (ev) =>
  ev?.metadata?.createdAt || null;

const getArchivedAt = (ev) =>
  ev?.metadata?.deletedFlag ? (ev?.metadata?.updatedAt || null) : null;

const matchesSearch = (event, term) => {
  const created = getCreatedByName(event).toLowerCase();
  const auditor = getAuditorName(event).toLowerCase();
  return (
    event.title?.toLowerCase().includes(term)          ||
    event.locationCode?.toLowerCase().includes(term)   ||
    auditor.includes(term) ||
    created.includes(term)
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   useSnack
═══════════════════════════════════════════════════════════════════════════ */

const useSnack = () => {
  const [snack,  setSnack]  = useState(null);
  const timerRef            = useRef(null);

  const show = useCallback((message, severity = "success") => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setSnack({ message, severity });
    timerRef.current = setTimeout(() => setSnack(null), 4000);
  }, []);

  const close = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setSnack(null);
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const Node = (
    <Snackbar
      open={!!snack}
      autoHideDuration={4000}
      onClose={close}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      {snack && (
        <Alert severity={snack.severity} onClose={close} variant="filled">
          {snack.message}
        </Alert>
      )}
    </Snackbar>
  );

  return { show, Node };
};

/* ═══════════════════════════════════════════════════════════════════════════
   ProgressBar
═══════════════════════════════════════════════════════════════════════════ */

const ProgressBar = React.memo(
  ({ value, verified, missing, shifted, total }) => {
    const pending = total - verified - missing - shifted;
    return (
      <Box sx={{ minWidth: 150 }}>
        <Box sx={{
          height: 6, borderRadius: 3,
          bgcolor: "rgba(0,0,0,0.06)", overflow: "hidden", mb: 0.5,
        }}>
          <Box sx={{
            height: "100%", width: `${value}%`, borderRadius: 3,
            bgcolor: value === 100 ? "#16a34a" : value > 50 ? "#2563eb" : "#d97706",
            transition: "width 0.4s ease",
          }} />
        </Box>
        <Stack direction="row" gap={0.75} flexWrap="wrap">
          <Typography variant="caption" fontWeight={700} color="text.secondary">{value}%</Typography>
          <Typography variant="caption" sx={{ color: "#15803d", fontSize: "0.7rem" }}>{verified} verified</Typography>
          <Typography variant="caption" sx={{ color: "#b91c1c", fontSize: "0.7rem" }}>{missing} missing</Typography>
          <Typography variant="caption" sx={{ color: "#b45309", fontSize: "0.7rem" }}>{shifted} shifted</Typography>
          <Typography variant="caption" sx={{ color: "#6b7280", fontSize: "0.7rem" }}>{pending} pending</Typography>
        </Stack>
      </Box>
    );
  },
  (prev, next) =>
    prev.value    === next.value    &&
    prev.verified === next.verified &&
    prev.missing  === next.missing  &&
    prev.shifted  === next.shifted  &&
    prev.total    === next.total,
);

/* ═══════════════════════════════════════════════════════════════════════════
   SKELETON
═══════════════════════════════════════════════════════════════════════════ */

const AuditTableSkeleton = () => (
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
      <Box>
        <Skeleton variant="text" width={220} height={40} />
        <Skeleton variant="text" width={340} height={20} />
      </Box>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Skeleton variant="rounded" width={160} height={40} />
        <Skeleton variant="rounded" width={130} height={40} />
      </Box>
    </Box>
    <Skeleton variant="rounded" width="100%" height={48} sx={{ borderRadius: 3, mb: 2 }} />
    <Box sx={{ mb: 2 }}>
      <Skeleton variant="rounded" width="40%" height={40} />
    </Box>
    <Box sx={{ background: "#fff", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
      <Box sx={{ display: "flex", gap: 2, py: 1.5, mb: 1, borderBottom: "2px solid #e2e8f0" }}>
        {[60, 180, 130, 140, 100, 170, 120, 120, 110, 120, 200, 130].map((w, i) => (
          <Skeleton key={i} variant="text" width={w} height={16} />
        ))}
      </Box>
      {Array.from({ length: 8 }, (_, i) => (
        <Box key={i} sx={{ display: "flex", gap: 2, py: 1.5, alignItems: "center", borderBottom: "1px solid #f1f5f9" }}>
          <Skeleton variant="text"     width={40}  height={14} />
          <Skeleton variant="text"     width={160} height={14} />
          <Skeleton variant="text"     width={110} height={14} />
          <Skeleton variant="text"     width={120} height={14} />
          <Skeleton variant="rounded"  width={80}  height={22} />
          <Skeleton variant="text"     width={150} height={14} />
          <Skeleton variant="rounded"  width={90}  height={22} />
          <Skeleton variant="text"     width={100} height={14} />
          <Skeleton variant="text"     width={90}  height={14} />
          <Skeleton variant="text"     width={100} height={14} />
          <Skeleton variant="text"     width={180} height={14} />
          <Skeleton variant="circular" width={30}  height={30} />
        </Box>
      ))}
    </Box>
  </Container>
);

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: "1px solid #e2e8f0",
}));

const StatusSelect = React.memo(({ value, onChange, disabled, isReadOnly, options }) => {
  const cfg = ASSET_STATUS_CFG[value] ?? ASSET_STATUS_CFG.pending;

  if (isReadOnly) {
    return (
      <Chip
        icon={cfg.icon} label={cfg.label} size="small"
        sx={{
          bgcolor: cfg.bgColor, color: cfg.color,
          border: `1px solid ${cfg.borderColor}`,
          fontWeight: 700, fontSize: "0.72rem",
          "& .MuiChip-icon": { color: cfg.color },
        }}
      />
    );
  }

  return (
    <FormControl size="small" fullWidth>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        sx={{
          borderRadius: 2, bgcolor: cfg.bgColor, color: cfg.color,
          fontWeight: 700, fontSize: "0.78rem", minHeight: 36,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: cfg.borderColor },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: cfg.color },
          "& .MuiSelect-select": { display: "flex", alignItems: "center", gap: 0.75, py: 0.75 },
        }}
      >
        {options.map((opt) => {
          const oCfg = ASSET_STATUS_CFG[opt.value] ?? ASSET_STATUS_CFG.pending;
          return (
            <MenuItem key={opt.value} value={opt.value}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: oCfg.color, flexShrink: 0 }} />
                <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
});

/* ═══════════════════════════════════════════════════════════════════════════
   AuditAssetsModal — FULL SCREEN
═══════════════════════════════════════════════════════════════════════════ */

const REMARK_DLG_TITLE_ID   = "remark-dialog-title";
const COMPLETE_DLG_TITLE_ID = "complete-dialog-title";

const fmtAuditedAt = (isoStr) => {
  if (!isoStr) return "—";
  try {
    return new Date(isoStr).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return "—";
  }
};

const AuditAssetsModal = ({ open, onClose, event, onEventUpdated, viewerRole }) => {
  const { currentUser } = useAuth();
  const { show: showSnack, Node: SnackNode } = useSnack();

  const isAdminView = viewerRole !== "auditor";
  const isCompleted = event?.status === "completed";
  const isReadOnly  = isAdminView || isCompleted;

  const [assetRows,     setAssetRows]     = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [busyId,        setBusyId]        = useState(null);
  const [searchTerm,    setSearchTerm]    = useState("");
  const [statusFilter,  setStatusFilter]  = useState("all");
  const [completeDlg,   setCompleteDlg]   = useState(false);
  const [completing,    setCompleting]    = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [ticketCreated, setTicketCreated] = useState(null);

  const [remarkDialog, setRemarkDialog] = useState({ open: false, row: null, newStatus: null });
  const [remarkText,   setRemarkText]   = useState("");
  const autoCompleteTriggered = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    const html = document.documentElement;
    const previousOverflow     = html.style.overflow;
    const previousPaddingRight = html.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    html.style.overflow = "hidden";
    if (scrollbarWidth > 0) html.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      html.style.overflow     = previousOverflow;
      html.style.paddingRight = previousPaddingRight;
    };
  }, [open]);

  useEffect(() => {
    let isMounted = true;
    const loadStatuses = async () => {
      try {
        const items = await singleValueListService.getItems(
          AUDIT_VERIFICATION_STATUSES_LIST_NAME,
          { companyId: currentUser?.companyId }
        );

        if (isMounted && Array.isArray(items) && items.length > 0) {
          const safeItems = [...items];
          const required = ["pending", "verified", "missing", "shifted"];

          required.forEach(req => {
            if (!safeItems.some(i => i.value === req)) {
              safeItems.push({ value: req, label: req.charAt(0).toUpperCase() + req.slice(1) });
            }
          });
          setStatusOptions(safeItems);
        }
      } catch (err) {
        console.warn("[AuditAssetsModal] Failed to load dynamic statuses:", err);
        setStatusOptions([
          { label: "Pending",  value: "pending"  },
          { label: "Verified", value: "verified" },
          { label: "Missing",  value: "missing"  },
          { label: "Shifted",  value: "shifted"  },
        ]);
      }
    };

    if (open) loadStatuses();
    return () => { isMounted = false; };
  }, [open, currentUser?.companyId]);

  const loadAssets = useCallback(async () => {
    const companyId = currentUser?.companyId;
    if (!event?.id || !companyId) return;
    setLoading(true);
    try {
      const rows = await fetchAuditEventAssets(event.id, companyId);
      setAssetRows(rows);
    } catch (err) {
      console.error("[AuditAssetsModal] loadAssets:", err);
      showSnack("Failed to load assets", "error");
    } finally {
      setLoading(false);
    }
  }, [event?.id, currentUser?.companyId, showSnack]);

  useEffect(() => {
    if (open) {
      autoCompleteTriggered.current = false;
      setSearchTerm("");
      setStatusFilter("all");
      setCompleteDlg(false);
      setTicketCreated(null);
      loadAssets();
    }
  }, [open, loadAssets]);

  const stats = useMemo(() => {
    const total    = assetRows.length;
    const verified = assetRows.filter((r) => r.status === "verified").length;
    const missing  = assetRows.filter((r) => r.status === "missing").length;
    const shifted  = assetRows.filter((r) => r.status === "shifted").length;
    const pending  = assetRows.filter((r) => !r.status || r.status === "pending").length;
    const done     = verified + missing + shifted;
    const progress = total > 0 ? Math.round((done / total) * 100) : 0;
    return { total, verified, missing, shifted, pending, done, progress };
  }, [assetRows]);

  const filtered = useMemo(() => {
    let result = assetRows;
    if (statusFilter !== "all") {
      result = result.filter((r) => (r.status || "pending") === statusFilter);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter((r) =>
        r.asset_name?.toLowerCase().includes(q)    ||
        r.asset_tag?.toLowerCase().includes(q)     ||
        r.category_id?.toLowerCase().includes(q)   || 
        r.brand_id?.toLowerCase().includes(q)      || 
        r.remark?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [assetRows, statusFilter, searchTerm]);

  /* ─────────────────────────────────────────────────────────────────────────
     persistStatusChange
  ───────────────────────────────────────────────────────────────────────── */
  const persistStatusChange = useCallback(
    async (row, newStatus, remark = "") => {
      setBusyId(row.id);
      try {
        const auditorDisplayName =
          currentUser.displayName ?? currentUser.name ?? currentUser.email ?? "Auditor";

        const updatedRow = await updateAuditHistoryStatus({
          historyRowId: row.id,
          auditEventId: event.id,
          assetId:      row.asset_id,
          assetName:    row.asset_name,
          companyId:    currentUser.companyId,
          locationCode: event.locationCode ?? "",
          auditorEmail: auditorDisplayName,
          auditorName:  auditorDisplayName,
          auditorId:    currentUser.uid,
          updatedBy:    currentUser.email || currentUser.uid || "system",
          newStatus,
          remark,
        });

        if (newStatus === "missing") {
          try {
            const ticketId = await helpDeskService.createMissingAssetTicket({
              companyId:       currentUser.companyId,
              auditorId:       currentUser.uid ?? currentUser.id ?? currentUser.userId ?? "",
              auditorEmail:    currentUser.email       ?? "",
              auditorName:     auditorDisplayName,
              assetId:         row.asset_id            ?? row.id ?? "",
              assetName:       row.asset_name          ?? "",
              assetTag:        row.asset_tag           ?? "",
              auditEventId:    event.id,
              auditEventTitle: event.title             ?? "",
              locationCode:    event.locationCode      ?? "",
              remark,
            });

            setTicketCreated({
              ticketId,
              assetName: row.asset_name ?? "Asset",
              shortId:   ticketId.substring(0, 8),
            });
          } catch (ticketErr) {
            console.error("[AuditAssetsModal] createMissingAssetTicket:", ticketErr);
            showSnack(
              "Asset marked missing but ticket creation failed — please raise manually",
              "warning",
            );
          }
        }

        setAssetRows((prev) =>
          prev.map((r) => {
            if (r.id !== row.id) return r;

            if (updatedRow) {
              return { ...r, ...updatedRow };
            }

            const now = new Date().toISOString();
            return {
              ...r,
              status:          newStatus,
              remark,
              updated_by:      currentUser.uid,
              updated_by_name: auditorDisplayName,
              updated_at:      now,
              audited_by:      r.audited_by      || currentUser.uid,
              audited_by_name: auditorDisplayName,
              auditor_email:   auditorDisplayName,
              audited_at:      now,
            };
          }),
        );

        if (newStatus === "missing") {
          showSnack("Asset marked as Missing — support ticket raised ✓", "error");
        } else {
          const msgMap = {
            verified: { text: "Asset marked as Verified ✓", sev: "success" },
            shifted:  { text: "Asset marked as Shifted",     sev: "warning" },
            pending:  { text: "Asset reset to Pending",      sev: "info"    },
          };
          const m = msgMap[newStatus] ?? { text: "Status updated", sev: "success" };
          showSnack(m.text, m.sev);
        }
      } catch (err) {
        console.error("[AuditAssetsModal] persistStatusChange:", err);
        showSnack("Failed to update asset — please try again", "error");
      } finally {
        setBusyId(null);
      }
    },
    [event, currentUser, showSnack],
  );

  const handleStatusSelect = useCallback((row, newStatus) => {
    if (newStatus === row.status) return;
    if (newStatus === "missing" || newStatus === "shifted") {
      setRemarkText(row.remark || "");
      setRemarkDialog({ open: true, row, newStatus });
      return;
    }
    persistStatusChange(row, newStatus, "");
  }, [persistStatusChange]);

  const handleRemarkConfirm = useCallback(() => {
    setRemarkDialog((current) => {
      const { row, newStatus } = current;
      if (!row || !newStatus) return current;
      persistStatusChange(row, newStatus, remarkText.trim());
      setRemarkText("");
      return { open: false, row: null, newStatus: null };
    });
  }, [remarkText, persistStatusChange]);

  useEffect(() => {
    if (
      !loading &&
      assetRows.length > 0 &&
      stats.pending === 0 &&
      !isReadOnly &&
      !autoCompleteTriggered.current
    ) {
      autoCompleteTriggered.current = true;
      setCompleteDlg(true);
    }
  }, [stats.pending, assetRows.length, loading, isReadOnly]);

  const handleCompleteAudit = useCallback(async () => {
    setCompleting(true);
    try {
      await updateAuditEvent(event.id, { status: "completed" });
      showSnack("Audit completed successfully!", "success");
      setCompleteDlg(false);
      onEventUpdated?.();
      setTimeout(() => onClose(), 800);
    } catch {
      showSnack("Failed to mark audit complete", "error");
    } finally {
      setCompleting(false);
    }
  }, [event, showSnack, onEventUpdated, onClose]);

  if (!open || !event) return null;

  const tableHeaders = [
    "#", "Asset Name", "Asset Tag", "Category",
    "Brand", "Remark",
    "Audited By", "Audited At",
    isReadOnly ? "Status" : "Status / Action",
  ];

  const headerBadge = isCompleted
    ? { label: "Completed (read-only)" }
    : isAdminView
      ? { label: "Admin View (read-only)" }
      : null;

  return (
    <>
      {SnackNode}

      <Dialog
        fullScreen open={open}
        onClose={busyId ? undefined : onClose}
        TransitionComponent={Transition}
        PaperProps={{ sx: { display: "flex", flexDirection: "column", overflow: "hidden" } }}
      >
        <AppBar sx={{ position: "relative", flexShrink: 0 }} elevation={1}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={onClose} disabled={!!busyId} aria-label="Close">
              <CloseIcon />
            </IconButton>
            <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                {isCompleted
                  ? <DoneAllIcon fontSize="small" />
                  : isAdminView
                    ? <AdminPanelSettingsIcon fontSize="small" />
                    : <AssignmentIcon fontSize="small" />}
                <Typography variant="h6" sx={{ lineHeight: 1.2 }} noWrap>{event.title}</Typography>
                {headerBadge && (
                  <Chip
                    label={headerBadge.label} size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 600, height: 22, fontSize: "0.7rem" }}
                  />
                )}
              </Stack>
              <Stack direction="row" gap={2} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                {event.locationCode && (
                  <Typography variant="caption" sx={{ opacity: 0.9, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LocationOnIcon sx={{ fontSize: 14 }} />{event.locationCode}
                  </Typography>
                )}
                {event.scheduled_date && (
                  <Typography variant="caption" sx={{ opacity: 0.9, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarMonthIcon sx={{ fontSize: 14 }} />
                    {new Date(event.scheduled_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </Typography>
                )}
                {getAuditorName(event) !== "—" && (
                  <Typography variant="caption" sx={{ opacity: 0.9, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PersonIcon sx={{ fontSize: 14 }} />{getAuditorName(event)}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Toolbar>
        </AppBar>

        {!isCompleted && isAdminView && (
          <Box sx={{ px: 3, py: 1.25, bgcolor: "#f5f3ff", borderBottom: "1px solid #ede9fe", flexShrink: 0 }}>
            <Container maxWidth="xl">
              <Stack direction="row" alignItems="center" gap={1}>
                <AdminPanelSettingsIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
                <Typography variant="caption" sx={{ color: "#6d28d9", fontWeight: 600 }}>
                  You are viewing this audit as an administrator. Asset statuses can only be updated by the assigned auditor.
                </Typography>
              </Stack>
            </Container>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", bgcolor: "#f1f5f9", flex: 1, minHeight: 0, overflow: "hidden" }}>
          <Box sx={{ flex: 1, overflowY: "auto", py: 3 }}>
            <Container maxWidth="xl">
              <Stack spacing={3}>

                {ticketCreated && (
                  <Alert
                    severity="error"
                    onClose={() => setTicketCreated(null)}
                    icon={false}
                    sx={{
                      borderRadius: 2,
                      border: "1px solid #fecaca",
                      bgcolor: "#fef2f2",
                      alignItems: "flex-start",
                      "& .MuiAlert-action": { pt: 0.5, alignSelf: "flex-start" },
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={700} color="#991b1b">
                        🎫 Support ticket auto-created for missing asset
                      </Typography>
                      <Typography variant="caption" color="#b91c1c">
                        Asset:{" "}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {ticketCreated.assetName}
                        </Box>
                        {" · "}Ticket ID:{" "}
                        <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                          #{ticketCreated.shortId}
                        </Box>
                      </Typography>
                      <Typography variant="caption" color="#b91c1c">
                        Visible in Help Desk — the admin has been notified.
                      </Typography>
                    </Stack>
                  </Alert>
                )}

                {!loading && assetRows.length > 0 && (
                  <SectionCard variant="outlined">
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          {stats.done} of {stats.total} assets audited
                        </Typography>
                        <Typography variant="body2" fontWeight={700}
                          sx={{ color: stats.progress === 100 ? "#16a34a" : "#2563eb" }}>
                          {stats.progress}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate" value={stats.progress}
                        sx={{
                          height: 10, borderRadius: 5, bgcolor: "#e5e7eb",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 5,
                            bgcolor: stats.progress === 100 ? "#16a34a" : "#2563eb",
                          },
                        }}
                      />
                      <Stack direction="row" gap={1.5} sx={{ mt: 2, flexWrap: "wrap" }}>
                        {[
                          { label: "Verified", count: stats.verified, color: "#16a34a" },
                          { label: "Missing",  count: stats.missing,  color: "#dc2626" },
                          { label: "Shifted",  count: stats.shifted,  color: "#d97706" },
                          { label: "Pending",  count: stats.pending,  color: "#6b7280" },
                        ].map((s) => (
                          <Box key={s.label} sx={{
                            display: "flex", alignItems: "center", gap: 1,
                            px: 1.5, py: 0.5, borderRadius: 1.5,
                            bgcolor: "#f8fafc", border: "1px solid #e2e8f0",
                            fontSize: "0.8125rem", color: "#475569",
                          }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color }} />
                            <strong>{s.count}</strong> {s.label}
                            {s.label === "Missing" && s.count > 0 && (
                              <Tooltip
                                title={`${s.count} support ticket${s.count !== 1 ? "s" : ""} auto-raised`}
                                arrow
                              >
                                <Box component="span" sx={{
                                  ml: 0.25, fontSize: "0.7rem", cursor: "help",
                                  opacity: 0.8,
                                }}>
                                  🎫
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </SectionCard>
                )}

                <SectionCard variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <TextField
                        fullWidth size="small"
                        placeholder="Search assets by name, tag, category, brand, remark…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ fontSize: 18, color: "action.active" }} />
                            </InputAdornment>
                          ),
                          endAdornment: searchTerm && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setSearchTerm("")} aria-label="Clear search">
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
                      />

                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        <Chip
                          label="All Statuses"
                          clickable
                          onClick={() => setStatusFilter("all")}
                          color={statusFilter === "all" ? "primary" : "default"}
                          variant={statusFilter === "all" ? "filled" : "outlined"}
                          sx={{ fontWeight: 600 }}
                        />
                        {statusOptions.map((opt) => {
                          const isSelected = statusFilter === opt.value;
                          const cfg = ASSET_STATUS_CFG[opt.value] ?? ASSET_STATUS_CFG.pending;
                          return (
                            <Chip
                              key={opt.value}
                              label={opt.label}
                              clickable
                              onClick={() => setStatusFilter(opt.value)}
                              icon={cfg.icon}
                              sx={{
                                fontWeight: 600,
                                bgcolor: isSelected ? cfg.bgColor : "#fff",
                                color: isSelected ? cfg.color : "#475569",
                                border: `1px solid ${isSelected ? cfg.borderColor : "#e2e8f0"}`,
                                "& .MuiChip-icon": { color: isSelected ? cfg.color : "#94a3b8" },
                                "&:hover": { bgcolor: isSelected ? cfg.bgColor : "#f8fafc" },
                              }}
                            />
                          );
                        })}
                      </Stack>
                    </Stack>
                  </CardContent>
                </SectionCard>

                <SectionCard variant="outlined">
                  <CardContent sx={{ p: 0 }}>
                    {loading ? (
                      <Box sx={{ p: 3 }}>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                          <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1, borderRadius: 1.5 }} />
                        ))}
                      </Box>
                    ) : filtered.length === 0 ? (
                      <Box sx={{ p: 8, textAlign: "center" }}>
                        <InventoryIcon sx={{ fontSize: 56, color: "text.disabled", mb: 1.5 }} />
                        <Typography variant="body1" color="text.secondary" fontWeight={500} mb={0.5}>
                          {assetRows.length === 0 ? "No assets seeded for this audit" : "No assets match your filters"}
                        </Typography>
                        {(searchTerm || statusFilter !== "all") && (
                          <Button
                            size="small" variant="outlined"
                            onClick={() => { setSearchTerm(""); setStatusFilter("all"); }}
                            sx={{ mt: 2, borderRadius: 2, textTransform: "none" }}
                          >
                            Clear Filters
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <TableContainer sx={{ maxHeight: "calc(100vh - 460px)" }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              {tableHeaders.map((h) => (
                                <TableCell key={h} sx={{
                                  fontWeight: 700, fontSize: "0.75rem",
                                  bgcolor: "#f3f4f6", color: "#374151",
                                  py: 1.25, whiteSpace: "nowrap",
                                }}>
                                  {h === "Audited By" ? (
                                    <Stack direction="row" alignItems="center" gap={0.5}>
                                      <PersonIcon sx={{ fontSize: 13, color: "#6b7280" }} />{h}
                                    </Stack>
                                  ) : h === "Audited At" ? (
                                    <Stack direction="row" alignItems="center" gap={0.5}>
                                      <AccessTimeIcon sx={{ fontSize: 13, color: "#6b7280" }} />{h}
                                    </Stack>
                                  ) : h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filtered.map((row, idx) => {
                              const isUpdating        = busyId === row.id;
                              const currentStatus     = row.status || "pending";
                              const hasBeenAudited   = currentStatus !== "pending";
                              const isMissing        = currentStatus === "missing";
                              const auditorNameDisplay = row.audited_by_name || row.auditor_email || "Auditor";
                              const auditedAtDisplay   = row.audited_at || row.metadata?.updatedAt;

                              return (
                                <TableRow
                                  key={row.id} hover
                                  sx={{
                                    opacity: isUpdating ? 0.6 : 1,
                                    transition: "opacity 0.2s",
                                    bgcolor: isMissing ? "#fff5f5" : "inherit",
                                  }}
                                >
                                  <TableCell sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                                    {idx + 1}
                                  </TableCell>

                                  <TableCell>
                                    <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                                      <Typography variant="body2" fontWeight={600}>
                                        {row.asset_name || "—"}
                                      </Typography>
                                      {isMissing && (
                                        <Tooltip title="Support ticket raised for this asset" arrow>
                                          <Box sx={{
                                            display: "inline-flex", alignItems: "center",
                                            px: 0.75, py: 0.125, borderRadius: 1,
                                            bgcolor: "#fef2f2", border: "1px solid #fecaca",
                                            fontSize: "0.6rem", fontWeight: 700,
                                            color: "#dc2626", whiteSpace: "nowrap", lineHeight: 1.6,
                                          }}>
                                            🎫 ticket raised
                                          </Box>
                                        </Tooltip>
                                      )}
                                    </Stack>
                                  </TableCell>

                                  <TableCell>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                      {row.asset_tag || "—"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption">{row.category_name || "—"}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption">{row.brand_name || "—"}</Typography>
                                  </TableCell>

                                  {/* Remark */}
                                  <TableCell sx={{ maxWidth: 200 }}>
                                    {row.remark ? (
                                      <Tooltip title={row.remark} arrow>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                          display: "block", overflow: "hidden", textOverflow: "ellipsis",
                                          whiteSpace: "nowrap", maxWidth: 180, fontStyle: "italic",
                                        }}>
                                          {row.remark}
                                        </Typography>
                                      </Tooltip>
                                    ) : (
                                      <Typography variant="caption" color="text.disabled" fontStyle="italic">—</Typography>
                                    )}
                                  </TableCell>

                                  {/* Audited By */}
                                  <TableCell sx={{ minWidth: 140 }}>
                                    {hasBeenAudited ? (
                                      <Stack direction="row" alignItems="center" gap={0.75}>
                                        <Box sx={{
                                          width: 24, height: 24, borderRadius: "50%", bgcolor: "#dbeafe",
                                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                        }}>
                                          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#1d4ed8", lineHeight: 1 }}>
                                            {auditorNameDisplay.charAt(0).toUpperCase()}
                                          </Typography>
                                        </Box>
                                        <Typography variant="caption" fontWeight={600} color="text.primary"
                                          sx={{ maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                          {auditorNameDisplay}
                                        </Typography>
                                      </Stack>
                                    ) : (
                                      <Typography variant="caption" color="text.disabled" fontStyle="italic">—</Typography>
                                    )}
                                  </TableCell>

                                  {/* Audited At */}
                                  <TableCell sx={{ minWidth: 120 }}>
                                    {hasBeenAudited ? (
                                      <Typography variant="caption" color="text.secondary">
                                        {fmtAuditedAt(auditedAtDisplay)}
                                      </Typography>
                                    ) : (
                                      <Typography variant="caption" color="text.disabled" fontStyle="italic">—</Typography>
                                    )}
                                  </TableCell>

                                  {/* Status / Action */}
                                  <TableCell sx={{ minWidth: 170 }}>
                                    <Stack direction="row" alignItems="center" gap={0.75}>
                                      <Box sx={{ flex: 1 }}>
                                        <StatusSelect
                                          value={currentStatus}
                                          onChange={(newStatus) => handleStatusSelect(row, newStatus)}
                                          disabled={isUpdating}
                                          isReadOnly={isReadOnly}
                                          options={statusOptions}
                                        />
                                      </Box>
                                      {isUpdating && <CircularProgress size={14} />}
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </SectionCard>

                {!loading && filtered.length > 0 && (
                  <Stack
                    direction="row" justifyContent="space-between" alignItems="center"
                    sx={{ px: 1 }} flexWrap="wrap" gap={1}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Showing <strong>{filtered.length}</strong> of{" "}
                      <strong>{assetRows.length}</strong> assets
                      {!isReadOnly && " · Update the status column to record your audit findings"}
                      {isAdminView && !isCompleted && " · Read-only admin view"}
                      {stats.missing > 0 && (
                        <Box component="span" sx={{ color: "#dc2626", ml: 1 }}>
                          · {stats.missing} support ticket{stats.missing !== 1 ? "s" : ""} auto-raised
                        </Box>
                      )}
                    </Typography>
                    <Tooltip
                      title={
                        '"Audited By" and "Audited At" record who first changed each asset\'s status ' +
                        "and are preserved even if the assigned auditor is later changed."
                      }
                      arrow
                    >
                      <Typography variant="caption" color="text.secondary"
                        sx={{ textDecoration: "underline dotted", cursor: "help" }}>
                        Why "Audited By"?
                      </Typography>
                    </Tooltip>
                  </Stack>
                )}

              </Stack>
            </Container>
          </Box>

          <Box sx={{
            borderTop: "1px solid #e2e8f0", backgroundColor: "#fff",
            py: 2, px: 3, boxShadow: "0 -2px 8px rgba(0,0,0,0.04)", flexShrink: 0,
          }}>
            <Container maxWidth={false} disableGutters>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <Box>
                  {!isReadOnly && stats.pending === 0 && stats.total > 0 && (
                    <Alert severity="success" sx={{ py: 0.25, px: 1.5, borderRadius: 2 }}>
                      All assets audited — ready to complete
                    </Alert>
                  )}
                  {isAdminView && !isCompleted && stats.total > 0 && (
                    <Alert severity="info" icon={<AdminPanelSettingsIcon />} sx={{ py: 0.25, px: 1.5, borderRadius: 2 }}>
                      {stats.pending} asset{stats.pending !== 1 ? "s" : ""} pending ·{" "}
                      {stats.done} of {stats.total} audited
                      {stats.missing > 0 && (
                        <> · <strong>{stats.missing}</strong> ticket{stats.missing !== 1 ? "s" : ""} raised</>
                      )}
                    </Alert>
                  )}
                </Box>
                <Stack direction="row" gap={1.5}>
                  <Button
                    onClick={onClose} disabled={!!busyId}
                    sx={{
                      px: 3, py: 1, borderRadius: "6px", textTransform: "none",
                      bgcolor: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db",
                      fontWeight: 500, "&:hover": { bgcolor: "#e5e7eb" },
                    }}
                  >
                    Close
                  </Button>
                  {!isReadOnly && stats.total > 0 && (
                    <Tooltip
                      title={stats.pending > 0 ? `${stats.pending} asset${stats.pending !== 1 ? "s" : ""} still pending` : ""}
                      arrow
                    >
                      <span>
                        <Button
                          variant="contained" onClick={() => setCompleteDlg(true)}
                          disabled={stats.pending > 0 || completing} disableElevation
                          startIcon={<DoneAllIcon />}
                          sx={{
                            px: 3, py: 1, borderRadius: "6px", textTransform: "none",
                            fontWeight: 600, minWidth: 180, bgcolor: "#16a34a",
                            "&:hover":    { bgcolor: "#15803d" },
                            "&:disabled": { bgcolor: "#86efac", color: "#fff" },
                          }}
                        >
                          Complete Audit
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
            </Container>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={remarkDialog.open}
        onClose={() => { setRemarkDialog({ open: false, row: null, newStatus: null }); setRemarkText(""); }}
        maxWidth="xs" fullWidth
        aria-labelledby={REMARK_DLG_TITLE_ID}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle id={REMARK_DLG_TITLE_ID} sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            {remarkDialog.newStatus === "missing"
              ? <ErrorOutlineIcon sx={{ color: "#dc2626" }} />
              : <SwapHorizIcon    sx={{ color: "#d97706" }} />}
            <Typography variant="h6" fontWeight={700}>
              Mark as {remarkDialog.newStatus === "missing" ? "Missing" : "Shifted"}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {remarkDialog.newStatus === "missing" && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              Marking as <strong>Missing</strong> will auto-raise a support ticket to admin
              with the details you provide below.
            </Alert>
          )}
          {remarkDialog.newStatus === "shifted" && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Marking as <strong>Shifted</strong> — please provide details about the new location.
            </Alert>
          )}
          {remarkDialog.row && (
            <Box sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: "#f9fafb", border: "1px solid #e5e7eb" }}>
              <Typography variant="body2" fontWeight={700}>{remarkDialog.row.asset_name}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                {remarkDialog.row.asset_tag}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth size="small" multiline rows={3}
            placeholder={
              remarkDialog.newStatus === "missing"
                ? "Last known location, details for admin…"
                : "New location, reason for the shift…"
            }
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => { setRemarkDialog({ open: false, row: null, newStatus: null }); setRemarkText(""); }}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained" onClick={handleRemarkConfirm} disableElevation
            sx={{
              borderRadius: 2, textTransform: "none", fontWeight: 700, flex: 1,
              bgcolor: remarkDialog.newStatus === "missing" ? "#dc2626" : "#d97706",
              "&:hover": { bgcolor: remarkDialog.newStatus === "missing" ? "#b91c1c" : "#b45309" },
            }}
          >
            Confirm {remarkDialog.newStatus === "missing" ? "Missing" : "Shifted"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={completeDlg} onClose={() => setCompleteDlg(false)}
        maxWidth="xs" fullWidth
        aria-labelledby={COMPLETE_DLG_TITLE_ID}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle id={COMPLETE_DLG_TITLE_ID} sx={{ textAlign: "center", pt: 3 }}>
          <DoneAllIcon sx={{ fontSize: 56, color: "#16a34a", mb: 1 }} />
          <Typography variant="h6" fontWeight={700}>Complete Audit?</Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {stats.verified} verified · {stats.missing} missing · {stats.shifted} shifted
            </Typography>
            {stats.missing > 0 && (
              <Alert severity="error" sx={{ borderRadius: 2, textAlign: "left" }}>
                <strong>{stats.missing}</strong> support ticket
                {stats.missing !== 1 ? "s have" : " has"} been auto-raised to admin
                for the missing asset{stats.missing !== 1 ? "s" : ""}.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setCompleteDlg(false)} sx={{ borderRadius: 2, textTransform: "none" }}>
            Review
          </Button>
          <Button
            variant="contained" color="success" onClick={handleCompleteAudit}
            disabled={completing} disableElevation
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, flex: 1 }}
          >
            {completing ? (
              <Stack direction="row" alignItems="center" gap={1}>
                <CircularProgress size={16} color="inherit" />
                Completing…
              </Stack>
            ) : "Complete Audit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   buildColumns
═══════════════════════════════════════════════════════════════════════════ */

const buildColumns = (isAuditor) => [
  { id: "sl_no",          label: "Sl.No",           minWidth: 60,  align: "center" },
  { id: "title",          label: "Audit Title",     minWidth: 180 },
  { id: "location",       label: "Location",        minWidth: 130 },
  ...(isAuditor ? [] : [{ id: "auditor", label: "Auditor", minWidth: 140 }]),
  { id: "assets",         label: "Assets",          minWidth: 90,  align: "center" },
  { id: "progress",       label: "Progress",        minWidth: 170 },
  { id: "status",         label: "Audit Status",    minWidth: 120 },
  ...(isAuditor ? [] : [{ id: "created_by", label: "Created By", minWidth: 130 }]),
  { id: "created_at",     label: "Created At",      minWidth: 120 },
  { id: "scheduled_date", label: "Scheduled Date",  minWidth: 130 },
  { id: "remark",         label: "Auditor Remarks", minWidth: 220 },
  { id: "actions",        label: "Actions",         minWidth: 110, align: "center" },
];

/* ═══════════════════════════════════════════════════════════════════════════
   AuditStatusFilterBar
═══════════════════════════════════════════════════════════════════════════ */
const AuditStatusFilterBar = React.memo(
  ({ value, onChange, counts }) => {
    return (
      <FormControl size="small" fullWidth>
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          sx={{
            borderRadius: 2,
            bgcolor: "#fff",
            fontWeight: 600,
            "& .MuiSelect-select": { py: 1.25 }
          }}
        >
          {AUDIT_STATUS_FILTER_KEYS.map((key) => {
            const cfg = AUDIT_STATUS_FILTER_CFG[key];
            const count = counts[key] ?? 0;
            const isActive = value === key;
            return (
              <MenuItem key={key} value={key}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    {cfg.icon}
                    <Typography variant="body2" fontWeight={600}>{cfg.label}</Typography>
                  </Stack>
                  <Chip
                    label={count}
                    size="small"
                    sx={{
                      height: 20, fontSize: "0.7rem", fontWeight: 700,
                      bgcolor: isActive ? (cfg.activeBg ?? "#f3f4f6") : "#f3f4f6",
                      color:   isActive ? (cfg.activeColor ?? "#374151") : "text.secondary",
                    }}
                  />
                </Stack>
              </MenuItem>
            );
          })}   
        </Select>
      </FormControl>
    );
  },
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT — AuditManagementView
═══════════════════════════════════════════════════════════════════════════ */

const AuditManagementView = () => {
  const { currentUser } = useAuth();
  const { show: showNotif, Node: SnackNode } = useSnack();

  const role      = currentUser?.role?.toLowerCase();
  const isAuditor = role === "auditor";
  const isAdmin   = !isAuditor;

  const [activeTab, setActiveTab] = useState("active");
  const [events,    setEvents]    = useState([]);
  const [archived,  setArchived]  = useState([]);
  const [loading,   setLoading]   = useState(true);

  const [modalOpen,       setModalOpen]       = useState(false);
  const [auditToEdit,     setAuditToEdit]     = useState(null);
  const [assetModalOpen,  setAssetModalOpen]  = useState(false);
  const [assetModalEvent, setAssetModalEvent] = useState(null);

  const [searchTerm,        setSearchTerm]        = useState("");
  const [auditStatusFilter, setAuditStatusFilter] = useState("all");

  const [page,              setPage]              = useState(0);
  const [rowsPerPage,       setRowsPerPage]       = useState(10);

  const [anchorEl,       setAnchorEl]       = useState(null);
  const [menuTarget,     setMenuTarget]     = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportMenuOpen = Boolean(exportAnchorEl);

  const [deleteTarget,   setDeleteTarget]   = useState(null);
  const [deleteConfirm,  setDeleteConfirm]  = useState(false);
  const [deleting,       setDeleting]       = useState(false);
  const [restoreTarget,  setRestoreTarget]  = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState(false);
  const [restoring,      setRestoring]      = useState(false);

  const loadEvents = useCallback(async () => {
    if (!currentUser?.companyId) return;
    setLoading(true);
    try {
      if (isAdmin) {
        const [active, arch] = await Promise.all([
          fetchAuditEvents(currentUser.companyId),
          fetchArchivedAuditEvents(currentUser.companyId),
        ]);
        setEvents(active);
        setArchived(arch);
      } else {
        const all = await fetchAuditEvents(currentUser.companyId);

        const myIds = [
          currentUser.uid,
          currentUser.id,
          currentUser.userId,
          currentUser.authUid,
        ]
          .filter(Boolean)
          .map(String);

        const mine = all.filter((e) => {
          const eid = String(e.auditor_id ?? "");
          return eid && myIds.includes(eid);
        });

        setEvents(mine);
      }
    } catch (err) {
      console.error("[AuditManagementView] load:", err);
      showNotif("Failed to load audit events", "error");
    } finally {
      setLoading(false);
    }
  }, [
    currentUser?.companyId,
    currentUser?.uid,
    currentUser?.id,
    currentUser?.userId,
    currentUser?.authUid,
    isAdmin,
    showNotif,
  ]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const isArchivedTab  = isAdmin && activeTab === "archived";
  const currentDataset = isArchivedTab ? archived : events;

  const COLS = useMemo(() => buildColumns(isAuditor), [isAuditor]);

  const auditStatusCounts = useMemo(() => {
    const base = { all: currentDataset.length, initiated: 0, in_progress: 0, completed: 0 };
    currentDataset.forEach((ev) => {
      const s = ev.status ?? "initiated";
      if (s in base) base[s]++;
    });
    return base;
  }, [currentDataset]);

  const filtered = useMemo(() => {
    let result = currentDataset;
    if (auditStatusFilter !== "all") {
      result = result.filter((e) => (e.status ?? "initiated") === auditStatusFilter);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result  = result.filter((e) => matchesSearch(e, q));
    }
    return result;
  }, [currentDataset, auditStatusFilter, searchTerm]);

  useEffect(() => { setPage(0); }, [activeTab, searchTerm, auditStatusFilter]);
  useEffect(() => { setAuditStatusFilter("all"); setSearchTerm(""); }, [activeTab]);

  const handleMenuOpen  = useCallback((e, ev) => { setAnchorEl(e.currentTarget); setMenuTarget(ev); }, []);
  const handleMenuClose = useCallback(() => { setAnchorEl(null); setMenuTarget(null); }, []);

  const handleEditClick = useCallback(() => {
    const target = menuTarget; handleMenuClose();
    setAuditToEdit(target); setModalOpen(true);
  }, [menuTarget, handleMenuClose]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false); setTimeout(() => setAuditToEdit(null), 300);
  }, []);

  const handleCreated = useCallback(() => {
    showNotif("Audit event created successfully"); loadEvents();
  }, [showNotif, loadEvents]);

  const handleUpdated = useCallback(() => {
    showNotif("Audit event updated successfully");
    loadEvents();
  }, [showNotif, loadEvents]);

  const handleDeleteClick = useCallback(() => {
    const target = menuTarget; handleMenuClose();
    setDeleteTarget(target); setDeleteConfirm(true);
  }, [menuTarget, handleMenuClose]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const actorId = currentUser?.uid ?? currentUser?.id ?? "";
      await softDeleteAuditEvent(deleteTarget.id, actorId);

      const now = new Date().toISOString();
      setEvents((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      setArchived((prev) => [{
        ...deleteTarget,
        metadata: {
          ...(deleteTarget.metadata || {}),
          deletedFlag: true,
          updatedAt:   now,
          updatedBy:   actorId || deleteTarget.metadata?.updatedBy || "system",
        },
      }, ...prev]);

      showNotif("Audit event archived successfully");
      setDeleteConfirm(false); setDeleteTarget(null);
    } catch (err) {
      console.error("[AuditManagementView] softDelete:", err);
      showNotif("Failed to archive audit event", "error");
    } finally {
      setDeleting(false);
      loadEvents();
    }
  }, [deleteTarget, currentUser, showNotif, loadEvents]);

  const handleRestoreClick = useCallback(() => {
    const target = menuTarget; handleMenuClose();
    setRestoreTarget(target); setRestoreConfirm(true);
  }, [menuTarget, handleMenuClose]);

  const handleRestoreConfirm = useCallback(async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const actorId = currentUser?.uid ?? currentUser?.id ?? "";
      await restoreAuditEvent(restoreTarget.id, actorId);

      const now = new Date().toISOString();
      setArchived((prev) => prev.filter((e) => e.id !== restoreTarget.id));
      setEvents((prev) => [{
        ...restoreTarget,
        metadata: {
          ...(restoreTarget.metadata || {}),
          deletedFlag: false,
          updatedAt:   now,
          updatedBy:   actorId || restoreTarget.metadata?.updatedBy || "system",
        },
      }, ...prev]);

      showNotif("Audit event restored successfully");
      setRestoreConfirm(false); setRestoreTarget(null);
    } catch (err) {
      console.error("[AuditManagementView] restore:", err);
      showNotif("Failed to restore audit event", "error");
    } finally {
      setRestoring(false);
      loadEvents();
    }
  }, [restoreTarget, currentUser, showNotif, loadEvents]);

  const handleExportCSV = useCallback(() => {
    if (!filtered.length) { showNotif("Nothing to export", "warning"); return; }
    const headers = [
      "Title", "Location", "Auditor", "Scheduled Date", "Audit Status",
      "Total Assets", "Verified", "Missing", "Shifted", "Pending", "Progress %",
      "Created By", "Created At", "Auditor Remarks",
      ...(isArchivedTab ? ["Archived At"] : []),
    ];
    const rows = filtered.map((e) => {
      const total    = e.totalAssets    ?? 0;
      const verified = e.verifiedAssets ?? 0;
      const missing  = e.missingAssets  ?? 0;
      const shifted  = e.shiftedAssets  ?? 0;
      const pending  = total - verified - missing - shifted;
      const createdAt = getCreatedAt(e);
      const archivedAt = getArchivedAt(e);
      return [
        sanitizeCSV(e.title),
        sanitizeCSV(e.locationCode),
        sanitizeCSV(getAuditorName(e)),
        e.scheduled_date ? new Date(e.scheduled_date).toLocaleDateString("en-GB") : "",
        sanitizeCSV(e.status),
        total, verified, missing, shifted, pending, e.progress ?? 0,
        sanitizeCSV(getCreatedByName(e)),
        createdAt ? new Date(createdAt).toLocaleDateString("en-GB") : "",
        `"${(e.remarks ?? []).join(" | ").replace(/"/g, '""')}"`,
        ...(isArchivedTab ? [archivedAt ? new Date(archivedAt).toLocaleDateString("en-GB") : ""] : []),
      ];
    });
    const csv  = "\uFEFF" + [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href  = URL.createObjectURL(blob);
    link.setAttribute(
      "download",
      `Audit_Events_${isArchivedTab ? "archived" : "active"}_${new Date().toISOString().split("T")[0]}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    showNotif("CSV exported successfully");
  }, [filtered, isArchivedTab, showNotif]);

  const handleExportPDF = useCallback(async () => {
    if (!filtered.length) { showNotif("Nothing to export", "warning"); return; }
    try {
      const { jsPDF } = await getPDFLib();
      const doc = new jsPDF("landscape");
      doc.setFontSize(16); doc.setTextColor(30, 30, 30);
      doc.text(`Audit Events Report — ${isArchivedTab ? "Archived" : "Active"}`, 14, 15);
      doc.setFontSize(9); doc.setTextColor(120, 120, 120);
      doc.text(
        `Generated: ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}   |   Total records: ${filtered.length}`,
        14, 22,
      );
      const head = [[
        "Title", "Location", "Auditor", "Scheduled", "Status",
        "Total", "Verified", "Missing", "Shifted", "Pending", "Progress %",
        "Created By", "Created At", "Auditor Remarks",
        ...(isArchivedTab ? ["Archived At"] : []),
      ]];
      const body = filtered.map((e) => {
        const total    = e.totalAssets    ?? 0;
        const verified = e.verifiedAssets ?? 0;
        const missing  = e.missingAssets  ?? 0;
        const shifted  = e.shiftedAssets  ?? 0;
        const pending  = total - verified - missing - shifted;
        const createdAt = getCreatedAt(e);
        const archivedAt = getArchivedAt(e);
        return [
          e.title || "",
          e.locationCode || "",
          getAuditorName(e),
          e.scheduled_date ? new Date(e.scheduled_date).toLocaleDateString("en-GB") : "—",
          e.status || "",
          total, verified, missing, shifted, pending, `${e.progress ?? 0}%`,
          getCreatedByName(e),
          createdAt ? new Date(createdAt).toLocaleDateString("en-GB") : "—",
          (e.remarks ?? []).join(" | "),
          ...(isArchivedTab ? [archivedAt ? new Date(archivedAt).toLocaleDateString("en-GB") : "—"] : []),
        ];
      });
      doc.autoTable({
        startY: 28, head, body, theme: "grid",
        headStyles: { fillColor: [37, 99, 235], fontSize: 8, fontStyle: "bold", textColor: [255, 255, 255] },
        styles: { fontSize: 7.5, cellPadding: 2, overflow: "linebreak" },
        columnStyles: {
          0: { cellWidth: 36 }, 1: { cellWidth: 26 }, 2: { cellWidth: 26 }, 4: { cellWidth: 20 },
          13: { cellWidth: 40 },   
        },
        didParseCell(data) {
          if (data.section === "body" && data.column.index === 4) {
            const s = data.cell.raw;
            if (s === "completed")   data.cell.styles.textColor = [22, 163, 74];
            if (s === "in_progress") data.cell.styles.textColor = [37, 99, 235];
            if (s === "initiated")   data.cell.styles.textColor = [107, 114, 128];
          }
        },
      });
      doc.save(`Audit_Events_${isArchivedTab ? "archived" : "active"}_${new Date().toISOString().slice(0, 10)}.pdf`);
      showNotif("PDF exported successfully");
    } catch (err) {
      console.error("[AuditManagementView] exportToPDF:", err);
      showNotif("Failed to generate PDF. Please try again.", "error");
    }
  }, [filtered, isArchivedTab, showNotif]);

  const handleOpenAssetModal    = useCallback((ev) => { setAssetModalEvent(ev); setAssetModalOpen(true); }, []);
  const handleAssetModalUpdated = useCallback(() => { loadEvents(); }, [loadEvents]);
  const handleChangePage        = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  if (loading) return <AuditTableSkeleton />;

  const paginated       = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const hasActiveFilters = auditStatusFilter !== "all" || !!searchTerm.trim();

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: "flex", flexDirection: "column", gap: 3 }}>
      {SnackNode}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: "text.primary", letterSpacing: "-0.5px" }}>
            {isAuditor ? "My Audits" : "Audit Management"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isAuditor
              ? "Audits assigned to you — click Start / Continue to mark assets"
              : "Create, assign and track asset audit events across your organisation"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          {isAdmin && !isArchivedTab && (
            <Button
              variant="contained" color="primary" startIcon={<AddIcon />}
              onClick={() => { setAuditToEdit(null); setModalOpen(true); }}
              disableElevation
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, px: 2.5 }}
            >
              New Audit Event
            </Button>
          )}
          <Button
            variant="outlined" color="secondary"
            startIcon={<FileDownloadIcon />} endIcon={<KeyboardArrowDownIcon />}
            onClick={(e) => setExportAnchorEl(e.currentTarget)}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            Export
          </Button>
          <Menu
            id="audit-export-menu" anchorEl={exportAnchorEl} open={exportMenuOpen}
            onClose={() => setExportAnchorEl(null)} TransitionComponent={Fade}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { minWidth: 180, borderRadius: 2.5, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", mt: 0.5 } }}
          >
            <MenuItem onClick={() => { setExportAnchorEl(null); handleExportCSV(); }} sx={{ gap: 1.5, py: 1 }}>
              <FileDownloadIcon fontSize="small" color="action" />
              <Typography variant="body2" fontWeight={500}>Export CSV</Typography>
            </MenuItem>
            <MenuItem onClick={() => { setExportAnchorEl(null); handleExportPDF(); }} sx={{ gap: 1.5, py: 1 }}>
              <PictureAsPdfIcon fontSize="small" color="error" />
              <Typography variant="body2" fontWeight={500}>Export PDF</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {isAdmin && (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Tabs
            value={activeTab} onChange={(_, v) => setActiveTab(v)}
            sx={{
              px: 2,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.9rem", minHeight: 48 },
              "& .MuiTabs-indicator": { height: 3, borderRadius: 2 },
            }}
          >
            <Tab value="active" label={
              <Stack direction="row" alignItems="center" gap={1}>
                <EventNoteIcon sx={{ fontSize: 18 }} /><span>Active</span>
                <Chip label={events.length} size="small" sx={{
                  height: 20, fontSize: "0.7rem", fontWeight: 700,
                  bgcolor: activeTab === "active" ? "#dbeafe" : "#f3f4f6",
                  color:   activeTab === "active" ? "#1d4ed8" : "text.secondary",
                }} />
              </Stack>
            } />
            <Tab value="archived" label={
              <Stack direction="row" alignItems="center" gap={1}>
                <ArchiveIcon sx={{ fontSize: 18 }} /><span>Archived</span>
                <Chip label={archived.length} size="small" sx={{
                  height: 20, fontSize: "0.7rem", fontWeight: 700,
                  bgcolor: activeTab === "archived" ? "#fef3c7" : "#f3f4f6",
                  color:   activeTab === "archived" ? "#92400e" : "text.secondary",
                }} />
              </Stack>
            } />
          </Tabs>
        </Paper>
      )}

      {isArchivedTab && (
        <Alert severity="info" icon={<ArchiveIcon />} sx={{ borderRadius: 2.5 }}>
          Showing archived audit events. All data is preserved — you can restore any audit back to the active list.
        </Alert>
      )}

      <Paper sx={{ p: 2.5, borderRadius: 3, bgcolor: "background.paper" }} variant="outlined">
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
          <Box sx={{ flex: 1, width: "100%" }}>
            <TextField
              fullWidth size="small" variant="outlined" label="Search Audits"
              placeholder={
                isAuditor
                  ? "Search by title, location..."
                  : `Search ${isArchivedTab ? "archived" : "active"} audits...`
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "action.active" }} fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Box>

          <Box sx={{ minWidth: 240, width: { xs: "100%", md: "auto" } }}>
            <AuditStatusFilterBar
              value={auditStatusFilter}
              onChange={(key) => { setAuditStatusFilter(key); setPage(0); }}
              counts={auditStatusCounts}
            />
          </Box>
        </Stack>

        {hasActiveFilters && (
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Found <strong>{filtered.length}</strong> audit{filtered.length !== 1 ? "s" : ""}
              {searchTerm && <> matching <em>"{searchTerm}"</em></>}
              {auditStatusFilter !== "all" && (
                <> with status <strong>{AUDIT_STATUS_FILTER_CFG[auditStatusFilter]?.label}</strong></>
              )}
            </Typography>
            <Button
              size="small"
              startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
              onClick={() => { setSearchTerm(""); setAuditStatusFilter("all"); }}
              sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.25, color: "#6b7280", "&:hover": { color: "#374151", bgcolor: "transparent" } }}
            >
              Clear all
            </Button>
          </Stack>
        )}
      </Paper>

      <TableContainer
        component={Paper} variant="outlined"
        sx={{
          borderRadius: 3, boxShadow: "0px 4px 16px rgba(0,0,0,0.02)", overflowX: "auto",
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.12)", borderRadius: 4 },
          ...(isArchivedTab && { bgcolor: "#fafafa" }),
        }}
      >
        {filtered.length === 0 ? (
          <Box sx={{ p: 8, textAlign: "center" }}>
            <Alert severity="info" sx={{ justifyContent: "center", borderRadius: 2.5 }}>
              {hasActiveFilters ? (
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                  <span>No audits match your current filters.</span>
                  <Button
                    size="small"
                    onClick={() => { setSearchTerm(""); setAuditStatusFilter("all"); }}
                    sx={{ textTransform: "none", fontSize: "0.78rem", p: 0, minWidth: 0, fontWeight: 700 }}
                  >
                    Clear filters
                  </Button>
                </Stack>
              ) : searchTerm ? "No audits match your search."
                : isArchivedTab ? "No archived audits."
                  : isAuditor ? "No audits assigned to you yet."
                    : "No audit events yet. Create one to get started."}
            </Alert>
          </Box>
        ) : (
          <Table sx={{ minWidth: 1600 }} size="medium">
            <TableHead>
              <TableRow>
                {COLS.map((col) => (
                  <TableCell
                    key={col.id} align={col.align ?? "left"}
                    sx={{ fontWeight: 700, bgcolor: "background.neutral", color: "text.primary", whiteSpace: "nowrap", minWidth: col.minWidth }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((ev, index) => {
                const cfg         = EVENT_STATUS[ev.status] ?? EVENT_STATUS.initiated;
                const verified    = ev.verifiedAssets ?? 0;
                const missing     = ev.missingAssets  ?? 0;
                const shifted     = ev.shiftedAssets  ?? 0;
                const total       = ev.totalAssets    ?? 0;
                const remarks     = ev.remarks        ?? [];
                const isCompleted = ev.status === "completed";
                const createdAt   = getCreatedAt(ev);
                const createdBy   = getCreatedByName(ev);
                const auditorName = getAuditorName(ev);
                const archivedAt  = getArchivedAt(ev);

                return (
                  <TableRow key={ev.id} hover sx={{ ...(isArchivedTab && { opacity: 0.85 }) }}>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} color="text.primary">{ev.title || "—"}</Typography>
                        {ev.description && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: "block" }}>
                            {ev.description}
                          </Typography>
                        )}
                        {isArchivedTab && archivedAt && (
                          <Chip
                            label={`Archived ${new Date(archivedAt).toLocaleDateString("en-GB")}`}
                            size="small" icon={<ArchiveIcon style={{ fontSize: 12 }} />}
                            sx={{
                              height: 18, fontSize: "0.6rem", fontWeight: 600, mt: 0.5,
                              bgcolor: "#fef3c7", color: "#92400e",
                              "& .MuiChip-icon": { color: "#92400e" },
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.primary">{ev.locationCode || "—"}</Typography>
                    </TableCell>

                    {!isAuditor && (
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{auditorName}</Typography>
                      </TableCell>
                    )}

                    <TableCell align="center">
                      <Chip
                        label={`${total} assets`} size="small" variant="outlined"
                        icon={<InventoryIcon sx={{ fontSize: 13 }} />}
                        sx={{ fontWeight: 600, fontSize: "0.72rem" }}
                      />
                    </TableCell>

                    <TableCell>
                      <ProgressBar value={ev.progress ?? 0} verified={verified} missing={missing} shifted={shifted} total={total} />
                    </TableCell>

                    <TableCell>
                      <Chip label={cfg.label} size="small" color={cfg.color} icon={cfg.icon} sx={{ fontWeight: 600, fontSize: "0.72rem" }} />
                    </TableCell>

                    {!isAuditor && (
                      <TableCell>
                        <Typography variant="body2" color="text.primary">{createdBy}</Typography>
                      </TableCell>
                    )}

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {createdAt
                          ? new Date(createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                          : "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {ev.scheduled_date
                          ? new Date(ev.scheduled_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                          : "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 240 }}>
                      {remarks.length === 0 ? (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">No remarks yet</Typography>
                      ) : (
                        <Tooltip title={remarks.join(" • ")} arrow disableHoverListener={remarks.length <= 2}>
                          <Box>
                            {remarks.slice(0, 2).map((r, i) => (
                              <Typography key={i} variant="caption" color="text.secondary" sx={{
                                display: "block", mb: 0.25, overflow: "hidden",
                                textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220,
                              }}>
                                • {r}
                              </Typography>
                            ))}
                            {remarks.length > 2 && (
                              <Typography variant="caption" color="primary.main" fontWeight={600}>
                                +{remarks.length - 2} more
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      {isAuditor ? (
                        <Button
                          size="small" variant={isCompleted ? "outlined" : "contained"} disableElevation
                          startIcon={
                            isCompleted
                              ? <DoneAllIcon    sx={{ fontSize: 15 }} />
                              : ev.status === "in_progress"
                                ? <PlayArrowIcon  sx={{ fontSize: 15 }} />
                                : <AssignmentIcon sx={{ fontSize: 15 }} />
                          }
                          onClick={() => handleOpenAssetModal(ev)}
                          sx={{
                            borderRadius: 2, textTransform: "none", fontWeight: 700,
                            fontSize: "0.72rem", px: 1.5, py: 0.5, minWidth: 96,
                            ...(isCompleted
                              ? { borderColor: "#16a34a", color: "#16a34a", "&:hover": { bgcolor: "#f0fdf4" } }
                              : ev.status === "in_progress"
                                ? { bgcolor: "#d97706", "&:hover": { bgcolor: "#b45309" } }
                                : {}),
                          }}
                        >
                          {isCompleted ? "View" : ev.status === "in_progress" ? "Continue" : "Start"}
                        </Button>
                      ) : (
                        <Stack direction="row" gap={0.5} justifyContent="center">
                          <Tooltip title="View assets" arrow>
                            <IconButton size="small" onClick={() => handleOpenAssetModal(ev)} sx={{ color: "#2563eb", "&:hover": { bgcolor: "#eff6ff" } }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, ev)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {filtered.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Showing {page * rowsPerPage + 1} to{" "}
            {Math.min((page + 1) * rowsPerPage, filtered.length)} of{" "}
            {filtered.length} audit{filtered.length !== 1 ? "s" : ""}
            {hasActiveFilters && <> (filtered from <strong>{currentDataset.length}</strong> total)</>}
          </Typography>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]} component="div"
            count={filtered.length} rowsPerPage={rowsPerPage} page={page}
            onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ border: "none", overflow: "hidden" }}
          />
        </Box>
      )}

      {isAdmin && (
        <Menu
          anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
          TransitionComponent={Fade}
          PaperProps={{ sx: { minWidth: 200, borderRadius: 2.5, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)" } }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {isArchivedTab ? (
            <MenuItem onClick={handleRestoreClick} sx={{ gap: 1.5, py: 1 }}>
              <RestoreIcon fontSize="small" sx={{ color: "#16a34a" }} />
              <Typography variant="body2" fontWeight={500} sx={{ color: "#16a34a" }}>Restore Audit</Typography>
            </MenuItem>
          ) : (
            <>
              <MenuItem onClick={handleEditClick} sx={{ gap: 1.5, py: 1 }}>
                <EditIcon fontSize="small" color="action" />
                <Typography variant="body2" fontWeight={500}>Edit Audit</Typography>
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem onClick={handleDeleteClick} sx={{ gap: 1.5, py: 1, color: "warning.main" }}>
                <ArchiveIcon fontSize="small" sx={{ color: "#d97706" }} />
                <Typography variant="body2" fontWeight={600} sx={{ color: "#d97706" }}>Archive Audit</Typography>
              </MenuItem>
            </>
          )}
        </Menu>
      )}

      <Dialog
        open={deleteConfirm}
        onClose={() => { if (!deleting) { setDeleteConfirm(false); setDeleteTarget(null); } }}
        maxWidth="xs" fullWidth aria-labelledby="archive-dialog-title"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle id="archive-dialog-title" sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <ArchiveIcon sx={{ color: "#d97706" }} />
            <Typography variant="h6" fontWeight={700}>Archive Audit Event</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
            This audit will be <strong>hidden from the active list</strong> but{" "}
            <strong>no data will be deleted</strong>. You can restore it anytime from the <strong>Archived</strong> tab.
          </Alert>
          {deleteTarget && (
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#fffbeb", border: "1px solid #fde68a" }}>
              <Typography variant="body2" fontWeight={700} gutterBottom>{deleteTarget.title}</Typography>
              <Stack direction="row" gap={2} flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">📍 {deleteTarget.locationCode || "—"}</Typography>
                <Typography variant="caption" color="text.secondary">📦 {deleteTarget.totalAssets ?? 0} assets</Typography>
                <Typography variant="caption" color="text.secondary">👤 {getAuditorName(deleteTarget)}</Typography>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => { setDeleteConfirm(false); setDeleteTarget(null); }} disabled={deleting} sx={{ borderRadius: 2, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained" onClick={handleDeleteConfirm} disabled={deleting} disableElevation
            sx={{
              borderRadius: 2, textTransform: "none", fontWeight: 700, flex: 1, bgcolor: "#d97706",
              "&:hover": { bgcolor: "#b45309" }, "&:disabled": { bgcolor: "#fcd34d", color: "#fff" },
            }}
          >
            {deleting ? (
              <Stack direction="row" alignItems="center" gap={1}>
                <CircularProgress size={14} sx={{ color: "#fff" }} /><span>Archiving…</span>
              </Stack>
            ) : <><ArchiveIcon sx={{ fontSize: 16, mr: 0.75 }} />Yes, Archive</>}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={restoreConfirm}
        onClose={() => { if (!restoring) { setRestoreConfirm(false); setRestoreTarget(null); } }}
        maxWidth="xs" fullWidth aria-labelledby="restore-dialog-title"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle id="restore-dialog-title" sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <RestoreIcon sx={{ color: "#16a34a" }} />
            <Typography variant="h6" fontWeight={700}>Restore Audit Event</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }}>
            This audit will be <strong>restored to the active list</strong>.
            All original data, assignments and history will remain intact.
          </Alert>
          {restoreTarget && (
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
              <Typography variant="body2" fontWeight={700} gutterBottom>{restoreTarget.title}</Typography>
              <Stack direction="row" gap={2} flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">📍 {restoreTarget.locationCode || "—"}</Typography>
                <Typography variant="caption" color="text.secondary">📦 {restoreTarget.totalAssets ?? 0} assets</Typography>
                <Typography variant="caption" color="text.secondary">👤 {getAuditorName(restoreTarget)}</Typography>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => { setRestoreConfirm(false); setRestoreTarget(null); }} disabled={restoring} sx={{ borderRadius: 2, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained" onClick={handleRestoreConfirm} disabled={restoring} disableElevation
            sx={{
              borderRadius: 2, textTransform: "none", fontWeight: 700, flex: 1, bgcolor: "#16a34a",
              "&:hover": { bgcolor: "#15803d" }, "&:disabled": { bgcolor: "#86efac", color: "#fff" },
            }}
          >
            {restoring ? (
              <Stack direction="row" alignItems="center" gap={1}>
                <CircularProgress size={14} sx={{ color: "#fff" }} /><span>Restoring…</span>
              </Stack>
            ) : <><RestoreIcon sx={{ fontSize: 16, mr: 0.75 }} />Yes, Restore</>}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateEditAuditModal
        open={modalOpen} onClose={handleModalClose}
        auditToEdit={auditToEdit} onCreated={handleCreated} onUpdated={handleUpdated}
      />
      <AuditAssetsModal
        open={assetModalOpen}
        onClose={() => setAssetModalOpen(false)}
        event={assetModalEvent}
        onEventUpdated={handleAssetModalUpdated}
        viewerRole={role}
      />
    </Container>
  );
};

const AuditModule = () => <AuditManagementView />;
export default AuditModule;